import React, { useState, useEffect, useRef } from 'react';
import * as policyApi from '../services/policyApi';

// Stage → color mapping
const STAGE_COLORS = {
  QUEUED: { bg: '#F5F5F5', color: '#757575', label: 'Queued' },
  UPLOADING: { bg: '#E3F2FD', color: '#1565C0', label: 'Uploading' },
  VALIDATING: { bg: '#E3F2FD', color: '#1565C0', label: 'Validating' },
  BEDROCK_INGESTING: { bg: '#E3F2FD', color: '#1565C0', label: 'Ingesting' },
  BEDROCK_PARSING: { bg: '#E3F2FD', color: '#1565C0', label: 'Parsing' },
  INDEXING: { bg: '#E3F2FD', color: '#1565C0', label: 'Indexing' },
  REASSEMBLING: { bg: '#E3F2FD', color: '#1565C0', label: 'Reassembling' },
  ACTIVE: { bg: '#E8F5E9', color: '#2E7D32', label: 'Active' },
  FAILED: { bg: '#FFEBEE', color: '#C62828', label: 'Failed' },
};

const IN_PROGRESS_STAGES = new Set([
  'UPLOADING', 'VALIDATING', 'BEDROCK_INGESTING', 'BEDROCK_PARSING', 'INDEXING', 'REASSEMBLING',
]);

const TERMINAL_STAGES = new Set(['ACTIVE', 'FAILED']);

function formatElapsed(startIso) {
  if (!startIso) return '';
  const diff = Math.floor((Date.now() - new Date(startIso).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  return `${m}m ${s}s`;
}

function formatTime(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString();
}

/**
 * BatchStatusPanel
 *
 * Props:
 *   clientId — string
 *   batchId  — string | null
 */
const BatchStatusPanel = ({ clientId, batchId }) => {
  const [fileStatuses, setFileStatuses] = useState([]);
  const [batchStatus, setBatchStatus] = useState(null);
  const [retryingCategory, setRetryingCategory] = useState(null);
  const [retryError, setRetryError] = useState(null);
  const streamRef = useRef(null);
  const [, forceUpdate] = useState(0);

  // Tick every second to update elapsed times
  useEffect(() => {
    const timer = setInterval(() => forceUpdate((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!batchId || !clientId) return;

    // Reset state for new batch
    setFileStatuses([]);
    setBatchStatus(null);
    setRetryError(null);

    const stream = policyApi.openBatchStream(
      clientId,
      batchId,
      (data) => {
        // Polling fallback returns a full batch status object with a `files` array
        if (data && data.files) {
          // Normalise field names: polling uses progress_pct, SSE uses progress
          const normalised = data.files.map(f => ({
            ...f,
            progress: f.progress_pct ?? f.progress ?? 0,
            completed_at: f.terminal_at || f.completed_at || null,
            error_message: f.failure_reason || f.error_message || null,
          }));
          setFileStatuses(normalised);
          // Batch is complete when completed_at is set OR all files are terminal
          const allDone = normalised.every(f => f.stage === 'ACTIVE' || f.stage === 'FAILED');
          if (data.completed_at || allDone) {
            setBatchStatus('batch_complete');
          }
        } else if (data && data.category) {
          // Per-file SSE update
          setFileStatuses((prev) => {
            const idx = prev.findIndex((f) => f.category === data.category);
            const normalised = {
              ...data,
              progress: data.progress_pct ?? data.progress ?? 0,
              error_message: data.failure_reason || data.error_message || null,
            };
            if (idx >= 0) {
              const updated = [...prev];
              updated[idx] = { ...updated[idx], ...normalised };
              return updated;
            }
            return [...prev, normalised];
          });
        }
      },
      (err) => {
        console.warn('BatchStatusPanel stream error:', err.message);
      },
      () => {
        // batch_complete
        setBatchStatus('batch_complete');
        window.dispatchEvent(new CustomEvent('policy-reassembled', { bubbles: true }));
      }
    );

    streamRef.current = stream;

    return () => {
      if (streamRef.current) {
        streamRef.current.close();
        streamRef.current = null;
      }
    };
  }, [clientId, batchId]);

  const handleRetry = async (category) => {
    setRetryingCategory(category);
    setRetryError(null);
    try {
      await policyApi.retryBatchFile(clientId, batchId, category);
    } catch (err) {
      setRetryError(`Retry failed for ${category}: ${err.message}`);
    } finally {
      setRetryingCategory(null);
    }
  };

  if (!batchId) return null;

  const stageInfo = (stage) => STAGE_COLORS[stage] || { bg: '#F5F5F5', color: '#757575', label: stage || 'Unknown' };

  return (
    <div style={{ background: '#fafafa', borderRadius: '10px', border: '1px solid #e0e0e0', padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <h5 style={{ margin: 0, color: '#1a1a2e' }}>
          📊 Batch Progress
          <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#888', marginLeft: '8px' }}>
            {batchId}
          </span>
        </h5>
        {batchStatus === 'batch_complete' && (
          <span style={{ background: '#E8F5E9', color: '#2E7D32', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
            ✅ Complete
          </span>
        )}
      </div>

      {retryError && (
        <div style={{ background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px', color: '#C62828', fontSize: '12px' }}>
          ⚠️ {retryError}
        </div>
      )}

      {fileStatuses.length === 0 ? (
        <div style={{ color: '#888', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>
          ⏳ Waiting for batch updates…
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {fileStatuses.map((file) => {
            const stage = (file.stage || 'QUEUED').toUpperCase();
            const info = stageInfo(stage);
            const progress = typeof file.progress === 'number' ? file.progress : 0;
            const isInProgress = IN_PROGRESS_STAGES.has(stage);
            const isTerminal = TERMINAL_STAGES.has(stage);
            const isFailed = stage === 'FAILED';

            return (
              <div key={file.category} style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e8e8e8', padding: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: '#1a1a2e', textTransform: 'capitalize' }}>
                      {file.category?.replace(/_/g, ' ')}
                    </span>
                    {file.filename && (
                      <span style={{ fontSize: '11px', color: '#888', marginLeft: '8px' }}>
                        {file.filename}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      background: info.bg,
                      color: info.color,
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}>
                      {info.label}
                    </span>
                    {isFailed && (
                      <button
                        onClick={() => handleRetry(file.category)}
                        disabled={retryingCategory === file.category}
                        style={{
                          padding: '3px 10px',
                          background: retryingCategory === file.category ? '#ccc' : '#1565C0',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 600,
                          cursor: retryingCategory === file.category ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {retryingCategory === file.category ? '⏳ Retrying…' : '🔄 Retry'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ background: '#f0f0f0', borderRadius: '4px', height: '6px', overflow: 'hidden', marginBottom: '6px' }}>
                  <div style={{
                    width: `${progress}%`,
                    height: '100%',
                    background: isFailed ? '#F44336' : stage === 'ACTIVE' ? '#4CAF50' : '#1565C0',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease',
                  }} />
                </div>

                {/* Time info */}
                <div style={{ fontSize: '11px', color: '#888', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{progress}%</span>
                  {isInProgress && file.stage_started_at && (
                    <span>Elapsed: {formatElapsed(file.stage_started_at)}</span>
                  )}
                  {isTerminal && file.completed_at && (
                    <span>Completed: {formatTime(file.completed_at)}</span>
                  )}
                </div>

                {/* Error message */}
                {isFailed && file.error_message && (
                  <div style={{ fontSize: '11px', color: '#C62828', marginTop: '4px' }}>
                    {file.error_message}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BatchStatusPanel;
